// src/pages/admin/tabs/parts/AmenitiesPanel.jsx
import React from 'react';
import { AdminAPI } from '../../../../api/admin';
import { normalizeMediaUrl } from '../../../../api/base';
import { Card, Row2, Field, Actions, Btn, Badge, TableWrap, Table } from '../../ui';

export default function AmenitiesPanel({ propertyId, propertyType = 'regular' }) {
  const [links, setLinks] = React.useState([]);
  const [all, setAll] = React.useState([]);
  const [q, setQ] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');

  // Определяем API методы в зависимости от типа объекта
  const getApiMethods = () => {
    switch (propertyType) {
      case 'foreign':
        return {
          list: AdminAPI.listForeignPropertyAmenities,
          attach: AdminAPI.attachForeignPropertyAmenity,
          update: AdminAPI.updateForeignPropertyAmenity,
          delete: AdminAPI.deleteForeignPropertyAmenity,
        };
      case 'suburban':
        return {
          list: AdminAPI.listSuburbanPropertyAmenities,
          attach: AdminAPI.attachSuburbanPropertyAmenity,
          update: AdminAPI.updateSuburbanPropertyAmenity,
          delete: AdminAPI.deleteSuburbanPropertyAmenity,
        };
      case 'commercial-rent':
        return {
          list: AdminAPI.listCommercialRentPropertyAmenities,
          attach: AdminAPI.attachCommercialRentPropertyAmenity,
          update: AdminAPI.updateCommercialRentPropertyAmenity,
          delete: AdminAPI.deleteCommercialRentPropertyAmenity,
        };
      case 'parking':
        return {
          list: AdminAPI.listParkingPropertyAmenities,
          attach: AdminAPI.attachParkingPropertyAmenity,
          update: AdminAPI.updateParkingPropertyAmenity,
          delete: AdminAPI.deleteParkingPropertyAmenity,
        };
      default:
        return {
          list: AdminAPI.listPropertyAmenities,
          attach: AdminAPI.attachPropertyAmenity,
          update: AdminAPI.updatePropertyAmenity,
          delete: AdminAPI.deletePropertyAmenity,
        };
    }
  };

  const api = React.useMemo(() => getApiMethods(), [propertyType]);

  const load = React.useCallback(async ()=>{
    setBusy(true); setMsg('');
    try{
      const [l, a] = await Promise.all([
        api.list(propertyId, { locale:'ru' }),
        AdminAPI.listAmenities({ q: q || undefined, page_size: 100, is_active: 'true', locale: 'ru' }),
      ]);
      setLinks(l?.items || l || []);
      setAll((a?.items || a?.amenities || []).slice(0, 200));
    }catch(e){ setMsg(e.message || 'Не удалось загрузить удобства'); }
    finally{ setBusy(false); }
  }, [propertyId, q, api]);

  React.useEffect(()=>{ load(); }, [load]);

  const isAttached = (amenityId)=> links.some(l => String(l.amenity_id) === String(amenityId));

  const attach = async (amenityId)=>{
    setBusy(true);
    try{ await api.attach(propertyId, { amenity_id: amenityId }); await load(); }
    catch(e){ alert(e.message || 'Не удалось прикрепить'); }
    finally{ setBusy(false); }
  };
  const toggleActive = async (link)=>{
    await api.update(propertyId, link.id, { is_active: !link.is_active });
    await load();
  };
  const del = async (link)=>{
    if (!window.confirm('Убрать удобство из списка объекта?')) return;
    await api.delete(propertyId, link.id);
    await load();
  };
  const move = async (fromIdx, toIdx)=>{
    const arr = [...links];
    const [m] = arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, m);
    setLinks(arr);
    // сохраним порядок
    try{
      await AdminAPI.reorderPropertyAmenities(propertyId, arr.map(x=>x.id));
    }catch(e){
      console.error(e);
    }
  };

  return (
    <Card>
      <h3>Удобства объекта</h3>
      {msg && <div style={{color:'#777', marginBottom:8}}>{msg}</div>}

      <Row2>
        <Field>Поиск по каталогу удобств
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Поиск…"/>
        </Field>
      </Row2>

      <Card style={{background:'#fbfbfb', marginTop:8}}>
        <h4>Каталог удобств</h4>
        <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
          {all.map(a=>(
            <div key={a.id} style={{border:'1px solid var(--bdr)', borderRadius:10, padding:'8px 12px', display:'flex', gap:10, alignItems:'center', minWidth:200}}>
              {a.cover_thumb ? (
                <img src={normalizeMediaUrl(a.cover_thumb)} alt="" style={{width:32, height:32, objectFit:'cover', borderRadius:8}}/>
              ) : a.icon ? (
                <span style={{fontSize:20, width:32, height:32, display:'flex', alignItems:'center', justifyContent:'center'}}>{a.icon}</span>
              ) : (
                <div style={{width:32, height:32, background:'#f0f0f0', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16}}>🏢</div>
              )}
              <div style={{flex:1}}>
                <div style={{fontWeight:600, fontSize:14}}>{a.name || a.name_ru || a.name_en || a.slug}</div>
                {a.description && <div style={{fontSize:12, color:'#666', marginTop:2}}>{a.description.substring(0, 50)}{a.description.length > 50 ? '...' : ''}</div>}
              </div>
              <Btn onClick={()=>attach(a.id)} disabled={isAttached(a.id) || busy} style={{fontSize:12, padding:'4px 8px'}}>
                {isAttached(a.id) ? '✓' : '+'}
              </Btn>
            </div>
          ))}
          {!all.length && <div style={{color:'#777'}}>Ничего не найдено</div>}
        </div>
      </Card>

      <Card style={{marginTop:12}}>
        <h4>Привязанные к объекту</h4>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>#</th>
                <th>Иконка</th>
                <th>Название</th>
                <th>Активно</th>
                <th>Сортировка</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {links.map((l, idx)=>(
                <tr key={l.id}>
                  <td>{l.id}</td>
                  <td style={{fontSize:20}}>
                    {l.amenity?.icon || '—'}
                  </td>
                  <td style={{display:'flex', alignItems:'center', gap:8}}>
                    {l.amenity?.cover_thumb && <img src={normalizeMediaUrl(l.amenity.cover_thumb)} alt="" style={{width:40, height:40, objectFit:'cover', borderRadius:8}}/>}
                    {!l.amenity?.cover_thumb && l.amenity?.icon && <span style={{fontSize:24, width:40, height:40, display:'flex', alignItems:'center', justifyContent:'center'}}>{l.amenity.icon}</span>}
                    <div>
                      <div style={{fontWeight:600}}>{l.amenity?.name || l.amenity?.name_ru || l.amenity?.name_en || l.amenity?.slug || '—'}</div>
                      {l.amenity?.description && <div style={{fontSize:12, color:'#666', marginTop:2}}>{l.amenity.description}</div>}
                    </div>
                  </td>
                  <td>{l.is_active ? 'Да' : 'Нет'}</td>
                  <td style={{whiteSpace:'nowrap'}}>
                    <Btn onClick={()=>idx>0 && move(idx, idx-1)} disabled={idx===0}>↑</Btn>{' '}
                    <Btn onClick={()=>idx<links.length-1 && move(idx, idx+1)} disabled={idx===links.length-1}>↓</Btn>
                  </td>
                  <td style={{whiteSpace:'nowrap'}}>
                    <Btn onClick={()=>toggleActive(l)}>{l.is_active ? 'Выключить' : 'Включить'}</Btn>{' '}
                    <Btn onClick={()=>del(l)}>Убрать</Btn>
                  </td>
                </tr>
              ))}
              {!links.length && (
                <tr><td colSpan={6} style={{opacity:.7}}>Пока не добавили ни одного удобства</td></tr>
              )}
            </tbody>
          </Table>
        </TableWrap>
      </Card>
    </Card>
  );
}
